import React, { useEffect, useState, useCallback } from 'react';
import client from '../api/client';
import PaginationFooter from '../components/PaginationFooter';

const THX_RED = '#B71530';

export default function Suppliers() {
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [matching, setMatching] = useState(false);
  const [matchResult, setMatchResult] = useState(null);
  const [msg, setMsg] = useState('');
  const [search, setSearch] = useState('');
  const [sortDir, setSortDir] = useState('asc');
  const [pageSize, setPageSize] = useState(25);
  const [page, setPage] = useState(1);

  const fetchSuppliers = useCallback(() => {
    setLoading(true);
    client.get('/suppliers/')
      .then(r => setSuppliers(r.data.suppliers || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchSuppliers(); }, [fetchSuppliers]);

  const handleRunMatch = async () => {
    setMatching(true);
    setMatchResult(null);
    try {
      const r = await client.post('/suppliers/match');
      setMatchResult(r.data);
      setMsg('Matching completed');
      fetchSuppliers();
      setTimeout(() => setMsg(''), 4000);
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Match failed');
    } finally {
      setMatching(false);
    }
  };

  const filtered = (search
    ? suppliers.filter(s =>
        s.canonical_name?.toLowerCase().includes(search.toLowerCase()) ||
        s.account_code?.toLowerCase().includes(search.toLowerCase()) ||
        s.sage_account_code?.toLowerCase().includes(search.toLowerCase())
      )
    : [...suppliers]
  ).sort((a, b) => {
    const cmp = (a.canonical_name || '').localeCompare(b.canonical_name || '');
    return sortDir === 'asc' ? cmp : -cmp;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginated = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setPage(1);
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div className="d-flex gap-2 align-items-center">
          <input
            type="text"
            className="form-control form-control-sm"
            placeholder="Search suppliers..."
            style={{ width: '220px' }}
            value={search}
            onChange={handleSearch}
          />
        </div>
        <button
          className="btn btn-sm"
          style={{ backgroundColor: THX_RED, color: '#fff', border: 'none' }}
          onClick={handleRunMatch}
          disabled={matching}
        >
          {matching ? (
            <><span className="spinner-border spinner-border-sm me-1" /> Running Match...</>
          ) : (
            <><i className="bi bi-arrow-repeat me-1" /> Run Match Suppliers</>
          )}
        </button>
      </div>

      {msg && <div className="alert alert-info py-2 mb-3" style={{ fontSize: '14px' }}>{msg}</div>}

      {matchResult && (
        <div className="alert alert-success py-2 mb-3" style={{ fontSize: '13px' }}>
          Match complete: {matchResult.invoice_matching?.matched || 0} invoices matched,{' '}
          {matchResult.invoice_matching?.unmatched || 0} unmatched.
          Total suppliers: {matchResult.supplier_master?.total_suppliers || 0}.
        </div>
      )}

      <div className="card">
        <div className="card-header py-2">
          <span style={{ fontSize: '14px', fontWeight: '600' }}>Suppliers ({filtered.length})</span>
        </div>
        <div className="card-body p-0">
          {loading ? (
            <div className="text-center py-5"><div className="spinner-border text-danger spinner-border-sm" /></div>
          ) : (
            <div className="table-responsive">
              <table className="table table-hover table-sm mb-0" style={{ fontSize: '13px' }}>
                <thead className="table-light">
                  <tr>
                    <th
                      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}
                      onClick={() => setSortDir(d => d === 'asc' ? 'desc' : 'asc')}
                    >
                      Canonical Name{' '}
                      <i className={`bi bi-arrow-${sortDir === 'asc' ? 'up' : 'down'}`} style={{ fontSize: '11px' }} />
                    </th>
                    <th>Account Code</th>
                    <th>Sage Code</th>
                    <th>Syrinx Code</th>
                    <th>Source</th>
                    <th className="text-end">Invoices</th>
                    <th className="text-end">Aliases</th>
                  </tr>
                </thead>
                <tbody>
                  {paginated.length === 0 && (
                    <tr><td colSpan="7" className="text-center text-muted py-4">No suppliers found</td></tr>
                  )}
                  {paginated.map(s => (
                    <tr key={s.id}>
                      <td style={{ fontWeight: '500' }}>{s.canonical_name}</td>
                      <td>{s.account_code || '–'}</td>
                      <td>{s.sage_account_code || '–'}</td>
                      <td>{s.syrinx_account_code || '–'}</td>
                      <td>
                        <span className="badge bg-light text-dark border" style={{ fontSize: '11px' }}>
                          {s.source || '–'}
                        </span>
                      </td>
                      <td className="text-end">{s.invoice_count}</td>
                      <td className="text-end">{s.alias_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
        {!loading && filtered.length > 0 && (
          <PaginationFooter
            page={currentPage}
            totalPages={totalPages}
            total={filtered.length}
            pageSize={pageSize}
            onPageChange={setPage}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </div>
    </div>
  );
}
